import { faCircleNotch, faClose, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ActionDialog from "components/reusable/ActionDialog";
import BottomSheet from "components/reusable/BottomSheet";
import ErrorMessage from "components/reusable/ErrorMessage";
import OtpDialog from "components/reusable/OtpDialog";
import AuthContext from "context/AuthContext";
import { Button } from "flowbite-react";
import useToastContext from "hooks/useToastContext";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { apiDelete, apiPost } from "utils/apiFetch";
import pathObj from "utils/apiPath";
import { validationRegex } from "utils/constants";

const EditEmail = ({ onClose }) => {
  const { t } = useTranslation();
  const { user, getProfile, storeTokens } = useContext(AuthContext);
  const notification = useToastContext();
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onChange", shouldFocusError: true, defaultValues: {} });

  const [loading, setLoading] = useState(false);
  const [showWarningDialogEmail, setShowWarningDialogEmail] = useState(false);
  const [showWarningDeleteDialogEmail, setShowWarningDeleteDialogEmail] = useState(false);

  const [verifySecondaryEmailBody, setVerifySecondaryEmailBody] = useState({});
  const [showVerifySecondaryEmailOtp, setShowVerifySecondaryEmailOtp] = useState(false);

  const changePrimaryEmail = async () => {
    setLoading(true);
    try {
      const res = await apiPost(pathObj.changePrimaryEmail, {});
      if (res.data.success) {
        notification.success(res.data.message);
        getProfile();
        onClose();
      } else {
        notification.error(res.data.message);
      }
    } catch (err) {}
    setLoading(false);
  };

  const deleteSecondaryEmail = async () => {
    setLoading(true);
    try {
      const res = await apiDelete(pathObj.deleteSecondaryEmail);
      if (res.data.success) {
        notification.success(res.data.message);
        getProfile();
        onClose();
      }
    } catch (err) {}
    setLoading(false);
  };

  const preventSpace = (e) => {
    e.target.value = e.target.value.trim();
  };

  const onSecondaryEmailAdd = async (data) => {
    setLoading(true);
    try {
      var body = {
        secondaryEmail: data.secondary_email,
      };
      setVerifySecondaryEmailBody(body);
      const res = await apiPost(pathObj.updateSecondaryEmail, body);
      if (res.data.success) {
        notification.success(res.data.message);
        setShowVerifySecondaryEmailOtp(true);
      } else {
        notification.error(res.data.message);
      }
    } catch (err) {}
    setLoading(false);
  };

  return (
    <>
      <BottomSheet
        open={true}
        onClose={() => {
          onClose();
        }}
        title={t("SET_PRIMARY_EMAIL")}
      >
        <div className="font-medium pb-5">{t("SET_PRIMARY_EMAIL_WARNING")}</div>
        <div className="flex flex-col justify-between">
          <div className="flex gap-1">
            <input type="checkbox" checked onClick={() => false} />
            <span>{user.email}</span>
          </div>

          {user.secondaryEmail ? (
            <>
              <div className="flex justify-between items-center gap-1">
                <div className="flex gap-1">
                  <input
                    type="checkbox"
                    onClick={(e) => {
                      if (e.target.checked) {
                        setShowWarningDialogEmail(true);
                      }
                    }}
                  />
                  <span>{user.secondaryEmail}</span>
                </div>
                <FontAwesomeIcon
                  icon={faClose}
                  className="text-gray-600 cursor-pointer cursor-pointer"
                  onClick={() => {
                    setShowWarningDeleteDialogEmail(true);
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <form onSubmit={handleSubmit(onSecondaryEmailAdd)}>
                <div className="pt-5 pb-2 font-medium">{t("ADD_EMAIL")}</div>
                <div className="flex justify-between items-center">
                  <div className="w-full">
                    <input
                      type="text"
                      id="secondary_email"
                      disabled={loading}
                      className="inputform text-heading text-sm rounded-base block w-full px-3 py-2.5 shadow-xs placeholder:text-body"
                      placeholder={t("O_EMAIL")}
                      onKeyDown={(e) => preventSpace(e)}
                      {...register("secondary_email", {
                        required: t("REGISTER_EMAIL"),
                        pattern: {
                          value: validationRegex.email,
                          message: t("REGISTER_EMAIL_EX"),
                        },
                      })}
                      required
                    />
                    <ErrorMessage message={errors?.secondary_email?.message} />
                  </div>
                  {loading ? (
                    <FontAwesomeIcon icon={faCircleNotch} className="animate-spin text-gray-600" />
                  ) : (
                    <Button type="submit">
                      <FontAwesomeIcon icon={faPlus} className="text-gray-600 cursor-pointer cursor-pointer" />
                    </Button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </BottomSheet>
      {showWarningDialogEmail && (
        <ActionDialog
          open={showWarningDialogEmail}
          title={t("WARNING")}
          onPositive={() => {
            changePrimaryEmail();
            setShowWarningDialogEmail(false);
          }}
          onNegative={() => {
            setShowWarningDialogEmail(false);
          }}
          positiveText={t("CONFIRM")}
          negativeText={t("CANCEL")}
        >
          {t("SET_PRIMARY_EMAIL_CONFIRMATION")}
        </ActionDialog>
      )}
      {showWarningDeleteDialogEmail && (
        <ActionDialog
          open={showWarningDeleteDialogEmail}
          title={t("WARNING")}
          onPositive={() => {
            deleteSecondaryEmail();
            setShowWarningDeleteDialogEmail(false);
          }}
          onNegative={() => {
            setShowWarningDeleteDialogEmail(false);
          }}
          positiveText={t("CONFIRM")}
          negativeText={t("CANCEL")}
        >
          {t("CONFIRM_DELETE_SECONDARY_EMAIL")}
        </ActionDialog>
      )}
      {showVerifySecondaryEmailOtp && (
        <OtpDialog
          otpURL={pathObj.updateSecondaryEmail}
          otpConfirmURL={pathObj.verifySecondaryEmail}
          body={verifySecondaryEmailBody}
          onSuccess={(message) => {
            notification.success(message || t("OPERATION_SUCCESS"));
            getProfile();
            onClose();
          }}
          onClose={() => {
            onClose();
          }}
        />
      )}
    </>
  );
};

export default EditEmail;
